import { Map } from '../../../language/models';
import { DevPlanFieldAndAnswer, UpdateFieldAnswerRequest, DevPlanAction } from '../models';
import * as R from 'ramda';
import { keyFor } from '../../../core/api/module';
import { ActionType } from '../../../core/redux/actionType';
import { Dispatch } from 'react';
import { toMap } from '../../../language/arrays';
import { updateDevPlanFieldAnswerAction } from './api/updateDevPlanFieldAnswerAction';
import { AppState } from '../../../core/redux/appState';
import { COLORS } from '../../../globalStyle/colors';


/**
 * Filters out any actions from the given maybe array with the given action ID. If no array is given, an empty array is
 * returned.
 */
export const filterOutAction = (
  actions: DevPlanAction[] | undefined,
  actionId: number,
): DevPlanAction[] => actions?.filter(action => action.devActionId !== actionId) ?? [];


/**
 * Removes the action by the given ID associated to the given Dev Plan ID from the given map of actions, returning a new
 * map of actions. If the given map of actions is undefined, you get back an object with an empty array on it at the
 * given dev plan ID.
 */
export const removeAction = (
  devPlanId: number,
  actionId: number,
  actionsByItemId: Map<DevPlanAction[]> | undefined,
): Map<DevPlanAction[]> => R.assoc(
  `${devPlanId}`,
  filterOutAction(actionsByItemId?.[`${devPlanId}`], actionId),
  actionsByItemId || {},
);


/**
 * Applies the given function to the action in app state with the given ID. If no such action is found, state is
 * returned unchanged.
 */
export const updateAction = (
  devPlanItemId: number | string,
  actionId: number,
  updateFunc: (action: DevPlanAction) => DevPlanAction,
  state: AppState,
): AppState => {
  const updateIndex = state
                        .devPlan
                        ?.actionsByItemId
                        ?.[`${devPlanItemId}`]
                        ?.findIndex(action => action.devActionId === actionId)
                      ?? -1;

  if (updateIndex === -1) {
    return state;
  }

  const updateAction = state.devPlan!.actionsByItemId![`${devPlanItemId}`]![updateIndex];
  const newAction    = updateFunc(updateAction);

  return R.assocPath(
    ['devPlan', 'actionsByItemId', `${devPlanItemId}`],
    R.update(updateIndex, newAction, state.devPlan!.actionsByItemId![`${devPlanItemId}`]!),
    state,
  );
};


const addField = (
  mapping: Map<DevPlanFieldAndAnswer[]> | undefined,
  field: DevPlanFieldAndAnswer,
): Map<DevPlanFieldAndAnswer[]> => R.assoc(
  `${field.devActionId}`,
  (mapping?.[`${field.devActionId}`] ?? []).concat(field),
  mapping ?? {},
);


/**
 * Reduce a list of action fields/answers into a mapping: Dev plan item ID -> Action ID -> Field/Answer
 */
export const actionFieldsById = (
  fields: DevPlanFieldAndAnswer[],
): Map<Map<DevPlanFieldAndAnswer[]>> => fields.reduce(
  (running, next) => R.assoc(`${next.devItemId}`, addField(running[`${next.devItemId}`], next), running),
  {} as Map<Map<DevPlanFieldAndAnswer[]>>,
);


export const makeUpdateFieldAnswerRequestObj = (
  field: DevPlanFieldAndAnswer,
  newAnswer: string,
): UpdateFieldAnswerRequest => ({
  participantId:    field.participantId,
  projectId:        field.projectId,
  devItemId:        field.devItemId,
  devActionId:      field.devActionId,
  devFieldId:       field.devPlanFieldId,
  devFieldAnswerId: field.devPlanFieldAnswerId,
  sequence:         field.sequence,
  answer:           newAnswer.trim(),
}) as UpdateFieldAnswerRequest;


/**
 * Updates the given array of field answers (immutably) to include the new field/answer state given. Useful when the user
 * changes a field/answer and you need to update app state to reflect the changes.
 * @param fieldAnswers - The array of old field answers
 * @param newState - The new state to put into the array
 */
export const updateFieldAnswer = (
  fieldAnswers: DevPlanFieldAndAnswer[],
  newState: UpdateFieldAnswerRequest,
): DevPlanFieldAndAnswer[] => {
  const targetIndex = fieldAnswers.findIndex(
    item => item.devPlanFieldId === newState.devFieldId
            // is old-style answer and matches on the intersection of other properties
            || (
              !newState.devFieldId
              && item.sequence === newState.sequence
              && item.devActionId === newState.devActionId
              && item.devItemId === newState.devItemId
            ),
  );

  const newValue = R.mergeDeepRight<DevPlanFieldAndAnswer, UpdateFieldAnswerRequest>(
    fieldAnswers?.[targetIndex],
    newState,
  );

  return R.update(targetIndex, newValue, fieldAnswers);
};


/**
 * Dev plans have items which have actions which have questions which have answers. When updating an answer or answers
 * for a specific question, you get a loading/error state.
 * Shallow-memoized function that returns loading/error state for a given list of field/answers
 */
export const calcUpdateFieldAnswerApiState = <T>(fieldsAnswers: DevPlanFieldAndAnswer[]) => {
  let prevMap: Map<T> | undefined = undefined;
  let prevValue: boolean          = false;

  return (map: Map<T>): boolean => {
    if (map === prevMap) {
      return prevValue;
    }

    prevMap   = map;
    prevValue = R.any(
      field => !!map[keyFor(
        ActionType.UpdateDevPlanFieldAnswerRequest,
        `${field.devActionId}-${field.devPlanFieldId}`,
      )],
      fieldsAnswers,
    );

    return prevValue;
  };
};


export const dispatchFieldAnswerUpdates = (
  dispatch: Dispatch<any>,
  fieldsAnswers: DevPlanFieldAndAnswer[],
  dirtyFields: Map<string>,
): void => {
  const fields = toMap(fieldsAnswers, field => `${field.devPlanFieldId}`);

  Object
    .keys(dirtyFields)
    .map(fieldId => makeUpdateFieldAnswerRequestObj(fields[fieldId]!, dirtyFields[fieldId]))
    .map(updateDevPlanFieldAnswerAction)
    .forEach(dispatch);
};


export const colorForStatus = (status: number): string => {
  switch (status) {
    // Not Started
    case 0:
      return COLORS.RED;

    // In Progress
    case 1:
      return COLORS.GREEN;

    // Complete
    default:
      return COLORS.BLUE;
  }
};
